/**
 * Generic unit frame component.
 *
 * Unifies the frame container styling, empty-state presentation, HP bar,
 * resource bar, and cast bar layout across top unit frames (OwnFrame,
 * TargetFrame, FriendlyTargetFrame) and future frames.
 */

import type { CSSProperties, ReactElement, ReactNode } from "react";
import type { ChampionId, WireEntity } from "@pit/protocol";
import { DEFAULT_HUD_CHAMPION_ID } from "../champions/abilityLabels.js";
import { shieldAmount } from "./hud-util.js";
import { FrameCastBar } from "./FrameCastBar.js";
import { FrameResourceBar } from "./FrameResourceBar.js";
import { HpBar } from "./HpBar.js";
import { unitFrameStyle } from "./unitFrameScale.js";

export interface UnitFrameProps {
  entity: WireEntity | null;
  header?: ReactNode | ((entity: WireEntity | null) => ReactNode) | undefined;
  footer?: ReactNode | undefined;
  children?: ReactNode | undefined;
  testId: string;
  align?: "left" | "right" | undefined;
  championId?: ChampionId | undefined;
  accentColor?: string | undefined;
  borderColor?: string | undefined;
  emptyText?: string | undefined;
  dimWhenEmpty?: boolean | undefined;
  hpBarTestId?: string | undefined;
  resourceTestIdPrefix?: string | undefined;
  castBarTestIdPrefix?: string | undefined;
  extraAttributes?: Record<`data-${string}`, string | undefined> | undefined;
  style?: CSSProperties | undefined;
  tick?: number | undefined;
}

export function UnitFrame({
  entity,
  header,
  footer,
  children,
  testId,
  align = "left",
  championId,
  accentColor,
  borderColor,
  emptyText,
  dimWhenEmpty = true,
  hpBarTestId,
  resourceTestIdPrefix,
  castBarTestIdPrefix,
  extraAttributes,
  style,
  tick = 0,
}: UnitFrameProps): ReactElement {
  const isEmpty = entity === null;
  const border = borderColor ?? (accentColor ? `2px solid ${accentColor}` : "1px solid #444");
  const resolvedChampionId = championId ?? entity?.championId ?? DEFAULT_HUD_CHAMPION_ID;
  const prefix = testId.replace(/-frame$/, "");
  const resolvedHpBarTestId = hpBarTestId ?? `${prefix}-hp-bar`;
  const resolvedResourcePrefix = resourceTestIdPrefix ?? prefix;
  const resolvedCastBarPrefix = castBarTestIdPrefix ?? (testId.endsWith("-frame") ? testId : `${testId}-frame`);
  const renderedHeader = typeof header === "function" ? header(entity) : header;

  return (
    <div
      data-testid={testId}
      data-empty={isEmpty ? "true" : "false"}
      data-hp={entity === null ? "0" : String(entity.hp)}
      data-max-hp={entity === null ? "0" : String(entity.maxHp)}
      {...extraAttributes}
      style={{
        ...unitFrameStyle(align),
        width: 200,
        padding: 8,
        background: "#161c24",
        border,
        borderRadius: 4,
        ...(dimWhenEmpty && isEmpty ? { opacity: 0.4 } : {}),
        pointerEvents: "auto",
        ...style,
      }}
    >
      {renderedHeader}
      {isEmpty ? (
        emptyText !== undefined ? (
          <div style={{ fontSize: 12, opacity: dimWhenEmpty ? 0.7 : 0.5 }}>{emptyText}</div>
        ) : null
      ) : (
        <>
          <HpBar
            hp={entity.hp}
            maxHp={entity.maxHp}
            shieldAmount={shieldAmount(entity.auras)}
            testId={resolvedHpBarTestId}
          />
          <FrameResourceBar
            resource={entity.resource}
            points={entity.points}
            championId={resolvedChampionId}
            testIdPrefix={resolvedResourcePrefix}
          />
          <FrameCastBar
            cast={entity.cast}
            tick={tick}
            championId={resolvedChampionId}
            testIdPrefix={resolvedCastBarPrefix}
          />
          {children}
          {footer}
        </>
      )}
    </div>
  );
}
